/**
 * LeadMetricsPage — Today's metrics for the Lead Manager.
 *
 * Apr 28, 2026 (Squad-D). Screen 08 of 12.
 *
 * Sections:
 *  - Verification funnel (Inbound → Auto-verified → Manual-verified → Assigned → In-deal)
 *  - Verification time histogram
 *  - Source mix donut
 *  - Top product categories bars
 */

import { useEffect, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import { PageShell, Panel, EmptyState } from './_lmShared';

interface FunnelStage {
  label: string;
  value: number;
  prevPct?: number;
}

interface FunnelResp {
  stages?: FunnelStage[];
}

interface HistogramBin {
  label: string;
  count: number;
}

interface HistogramResp {
  bins?: HistogramBin[];
  medianMinutes?: number;
  p95Minutes?: number;
}

interface SourceMixSeg {
  label: string;
  count: number;
  color?: string;
}

interface SourceMixResp {
  total?: number;
  segments?: SourceMixSeg[];
}

interface ProductBar {
  product: string;
  count: number;
}

interface ProductMixResp {
  bars?: ProductBar[];
}

const STAGE_COLORS = ['var(--ink-0)', '#3A6A8C', '#C98A3B', '#2F7A5B', '#24604A'];

export function LeadMetricsPage() {
  const [funnel, setFunnel] = useState<FunnelStage[]>([]);
  const [hist, setHist] = useState<HistogramResp>({});
  const [sourceMix, setSourceMix] = useState<SourceMixResp>({});
  const [productMix, setProductMix] = useState<ProductBar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      safeGet<FunnelResp>('/analytics/lead-manager/funnel?period=today'),
      safeGet<HistogramResp>('/analytics/lead-manager/verification-time-histogram?period=today'),
      safeGet<SourceMixResp>('/analytics/lead-manager/source-mix?period=today'),
      safeGet<ProductMixResp>('/analytics/lead-manager/product-mix?period=today'),
    ]).then(([f, h, s, p]) => {
      setFunnel(f.data?.stages ?? []);
      setHist(h.data ?? {});
      setSourceMix(s.data ?? {});
      setProductMix(p.data?.bars ?? []);
      setError(!!(f.error && h.error && s.error && p.error));
      setLoading(false);
    });
  }, []);

  const today = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <PageShell
      title="Today's metrics"
      sub={`${today} · live · resets at 00:00 IST`}
      right={
        <>
          <button className="btn btn-outline">
            Today <ChevronDown size={12} />
          </button>
          <button className="btn btn-outline">
            <Download size={12} /> Export
          </button>
        </>
      }
    >
      {loading ? (
        <EmptyState title="Loading…" />
      ) : error ? (
        <EmptyState
          title="Metrics endpoints not online yet"
          sub="Backend will expose /analytics/lead-manager/* once Squad-A finishes the schema work. Cards below will populate automatically."
        />
      ) : (
        <>
          {/* Funnel */}
          <div className="card" style={{ padding: 18, marginBottom: 16 }}>
            <div
              style={{
                fontWeight: 500,
                marginBottom: 14,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span>Verification funnel</span>
              <div style={{ flex: 1 }} />
              <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                Inbound → Auto-verified → Manual-verified → Assigned → Deal-stage
              </span>
            </div>
            <Funnel stages={funnel} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: 16 }}>
            <Panel title="Verification time" sub="Minutes from inbound to verified">
              <Histogram
                bins={hist.bins ?? []}
                median={hist.medianMinutes}
                p95={hist.p95Minutes}
              />
            </Panel>
            <Panel
              title="Source mix"
              sub={`of today's ${sourceMix.total ?? 0} inbound`}
            >
              <SourceMix total={sourceMix.total ?? 0} segments={sourceMix.segments ?? []} />
            </Panel>
            <Panel title="Top product categories">
              <ProductBars rows={productMix} />
            </Panel>
          </div>
        </>
      )}
    </PageShell>
  );
}

/* ─── Charts ──────────────────────────────────────────────────────────── */

function Funnel({ stages }: { stages: FunnelStage[] }) {
  if (stages.length === 0) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
        No funnel data yet.
      </div>
    );
  }
  const max = Math.max(...stages.map((s) => s.value), 1);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 18,
        padding: '8px 0',
        height: 240,
      }}
    >
      {stages.map((s, i) => {
        const heightPct = (s.value / max) * 100;
        const prev = i > 0 ? stages[i - 1] : null;
        const pctOfPrev = prev && prev.value > 0 ? Math.round((s.value / prev.value) * 100) : null;
        return (
          <div
            key={s.label}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <div className="num-display" style={{ fontSize: 26 }}>
              {s.value}
            </div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 6, height: 16 }}>
              {pctOfPrev != null ? `${pctOfPrev}% of prev` : ' '}
            </div>
            <div
              style={{
                width: '70%',
                height: Math.max(8, (heightPct / 100) * 130),
                background: STAGE_COLORS[i % STAGE_COLORS.length],
                borderRadius: 4,
              }}
            />
            <div style={{ fontSize: 11, marginTop: 8, color: 'var(--ink-2)', fontWeight: 500 }}>
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Histogram({
  bins,
  median,
  p95,
}: {
  bins: HistogramBin[];
  median?: number;
  p95?: number;
}) {
  if (bins.length === 0) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
        No data yet.
      </div>
    );
  }
  const max = Math.max(...bins.map((b) => b.count), 1);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 130, padding: '4px 0' }}>
        {bins.map((b, i) => (
          <div
            key={b.label}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div
              style={{ fontSize: 10, color: 'var(--ink-3)', marginBottom: 4 }}
              className="num"
            >
              {b.count}
            </div>
            <div
              style={{
                width: '100%',
                height: Math.max(2, 100 * (b.count / max)),
                background: i < 3 ? 'var(--forest)' : 'var(--saffron)',
                borderRadius: '3px 3px 0 0',
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {bins.map((b) => (
          <div
            key={b.label}
            style={{ flex: 1, fontSize: 10, color: 'var(--ink-3)', textAlign: 'center' }}
            className="mono"
          >
            {b.label}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-3)' }}>
        Median{' '}
        <span className="num" style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
          {median != null ? `${median} min` : '—'}
        </span>{' '}
        · p95{' '}
        <span className="num" style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
          {p95 != null ? `${p95} min` : '—'}
        </span>
      </div>
    </div>
  );
}

function SourceMix({ total, segments }: { total: number; segments: SourceMixSeg[] }) {
  if (segments.length === 0 || total === 0) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
        No inbound today.
      </div>
    );
  }
  const colors = ['#25D366', '#F47216', '#3A6A8C', '#D4282E', '#8B4A6B', '#2F7A5B'];
  const segs = segments.map((s, i) => ({ ...s, color: s.color ?? colors[i % colors.length] }));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <Donut
        size={108}
        thickness={16}
        segments={segs.map((s) => ({ value: s.count, color: s.color! }))}
        center={<div className="num-display" style={{ fontSize: 22 }}>{total}</div>}
      />
      <div style={{ flex: 1 }}>
        {segs.map((s) => (
          <div
            key={s.label}
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, padding: '2px 0' }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
            <span style={{ flex: 1 }}>{s.label}</span>
            <span className="num" style={{ color: 'var(--ink-0)', fontWeight: 500 }}>
              {s.count}
            </span>
            <span className="num" style={{ color: 'var(--ink-3)', width: 32, textAlign: 'right' }}>
              {Math.round((s.count / total) * 100)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductBars({ rows }: { rows: ProductBar[] }) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-3)', fontSize: 12 }}>
        No product data yet.
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div>
      {rows.map((r) => (
        <div
          key={r.product}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}
        >
          <span style={{ width: 100, fontSize: 12, color: 'var(--ink-2)' }}>{r.product}</span>
          <div style={{ flex: 1, height: 8, background: 'var(--ivory-2)', borderRadius: 4 }}>
            <div
              style={{
                width: `${(r.count / max) * 100}%`,
                height: '100%',
                background: 'var(--ink-0)',
                borderRadius: 4,
              }}
            />
          </div>
          <span
            className="num"
            style={{ width: 32, textAlign: 'right', fontSize: 12, fontWeight: 500 }}
          >
            {r.count}
          </span>
        </div>
      ))}
    </div>
  );
}

function Donut({
  size,
  thickness,
  segments,
  center,
}: {
  size: number;
  thickness: number;
  segments: { value: number; color: string }[];
  center?: React.ReactNode;
}) {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {segments.map((s, i) => {
          const dash = (s.value / total) * c;
          const seg = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${dash} ${c}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </svg>
      {center && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {center}
        </div>
      )}
    </div>
  );
}

export default LeadMetricsPage;
